import axios from "axios";

// Strip trailing slashes: "https://host/" + "/api" would become "//api" and miss the API router.
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
  timeout: 120000,
  withCredentials: true,
});

// ---- Fix 7 (B8): session refresh -------------------------------------------------------------
// The access cookie lives 15 min, the refresh cookie 7 days. On a 401 of a logged-in user we call
// POST /auth/refresh once (shared by concurrent requests) and retry; if that fails the session
// really expired and AuthContext sends the user to /login with a notice.
let sessionActive = false;
let onSessionExpired = null;
let refreshing = null;
export const setSessionActive = (active, onExpired) => {
  sessionActive = !!active;
  if (onExpired !== undefined) onSessionExpired = onExpired;
};
export const refreshSession = () => {
  if (!refreshing) {
    refreshing = axios
      .post(`${API_BASE}/auth/refresh`, {}, { withCredentials: true })
      .then((r) => r.data?.user || null)
      .finally(() => setTimeout(() => { refreshing = null; }, 0));
  }
  return refreshing;
};
const NO_REFRESH = /\/auth\/(login|register|refresh|logout|me)\/?$/;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const cfg = error?.config;
    if (error?.response?.status !== 401 || !sessionActive || !cfg || cfg._retried || NO_REFRESH.test(cfg.url || "")) {
      return Promise.reject(error);
    }
    try {
      await refreshSession();
    } catch {
      sessionActive = false;
      if (onSessionExpired) onSessionExpired();
      return Promise.reject(error);
    }
    return api({ ...cfg, _retried: true });
  },
);

export const createSession = async (company, companyToken) => {
  const body = {};
  if (company) body.company = company;
  if (companyToken) body.company_token = companyToken;
  const { data } = await api.post("/sessions", body);
  return data;
};

export const getSession = async (sid) => {
  const { data } = await api.get(`/sessions/${sid}`);
  return data;
};

export const sendMessage = async (sid, text) => {
  const { data } = await api.post(`/sessions/${sid}/message`, { text });
  return data;
};

export const finalizeSession = async (sid) => {
  const { data } = await api.post(`/sessions/${sid}/finalize`);
  return data;
};

export const getReport = async (sid) => {
  const { data } = await api.get(`/sessions/${sid}/report`);
  return data;
};

export const excelUrl = (sid) => `${API_BASE}/sessions/${sid}/export/excel`;
export const chartUrl = (sid) => `${API_BASE}/sessions/${sid}/export/chart.png`;
export const chartPdfUrl = (sid) => `${API_BASE}/sessions/${sid}/export/chart.pdf`;

// ---- Dashboard & Agent config exports ----
export const dashboardExcelUrl = (window = 1) => `${API_BASE}/dashboard/export/excel?window=${window}`;
export const dashboardPdfUrl = (window = 1) => `${API_BASE}/dashboard/export/pdf?window=${window}`;
export const agentJsonUrl = (slug) => `${API_BASE}/agents/${slug}/export/json`;
export const agentMarkdownUrl = (slug) => `${API_BASE}/agents/${slug}/export/markdown`;
export const agentPdfUrl = (slug) => `${API_BASE}/agents/${slug}/export/pdf`;

// ---- Webhooks & events ----
export const getWebhookInfo = async (slug) => {
  const { data } = await api.get(`/agents/${slug}/webhook/info`);
  return data;
};

export const rotateWebhookSecret = async (slug) => {
  const { data } = await api.post(`/agents/${slug}/webhook/rotate`);
  return data;
};

export const listAgentEvents = async (slug, limit = 100) => {
  const { data } = await api.get(`/agents/${slug}/events`, { params: { limit } });
  return data;
};

// ---- Admin · Billing Plans ----
export const adminListPlans = async () => {
  const { data } = await api.get(`/admin/plans`);
  return data;
};
export const adminUnits = async () => {
  const { data } = await api.get(`/admin/plans/units`);
  return data;
};
export const adminCreatePlan = async (payload) => {
  const { data } = await api.post(`/admin/plans`, payload);
  return data;
};
export const adminUpdatePlan = async (id, payload) => {
  const { data } = await api.put(`/admin/plans/${id}`, payload);
  return data;
};
export const adminDeletePlan = async (id) => {
  const { data } = await api.delete(`/admin/plans/${id}`);
  return data;
};
export const adminAssignPlan = async (slug, planId) => {
  const { data } = await api.post(`/admin/agents/${slug}/plan`, { plan_id: planId || null });
  return data;
};
export const adminBilling = async (window = 1) => {
  const { data } = await api.get(`/admin/billing`, { params: { window } });
  return data;
};

// ---- KBL ----
export const kblSupported = async () => {
  const { data } = await api.get(`/kbl/supported`);
  return data;
};
export const kblList = async (company, department) => {
  const params = {};
  if (company) params.company = company;
  if (department) params.department = department;
  const { data } = await api.get(`/kbl`, { params });
  return data;
};
export const kblCompanies = async () => {
  const { data } = await api.get(`/kbl/companies`);
  return data;
};
export const kblUpload = async ({ file, company, department, tags }) => {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("company", company);
  fd.append("department", department);
  if (tags) fd.append("tags", tags);
  const { data } = await api.post(`/kbl/upload`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};
export const kblDelete = async (id) => {
  const { data } = await api.delete(`/kbl/${id}`);
  return data;
};
export const kblSearch = async ({ company, department, query, top_k = 5 }) => {
  const { data } = await api.post(`/kbl/search`, { company, department, query, top_k });
  return data;
};
export const kblReindex = async (company) => {
  const params = company ? { company } : {};
  const { data } = await api.post(`/kbl/reindex`, null, { params });
  return data;
};
export const kblStats = async (company) => {
  const params = company ? { company } : {};
  const { data } = await api.get(`/kbl/stats`, { params });
  return data;
};

// ----- Industry library (industry × department hints) -----
export const industryLibList = async (industry) => {
  const params = industry ? { industry } : {};
  const { data } = await api.get(`/admin/industry-library`, { params });
  return data;
};
export const industryLibUpsert = async (body) => {
  const { data } = await api.put(`/admin/industry-library`, body);
  return data;
};
export const industryLibDelete = async (hintId) => {
  const { data } = await api.delete(`/admin/industry-library/${hintId}`);
  return data;
};
export const industryCuratorDepartments = async () => {
  const { data } = await api.get(`/admin/industry-library/curator/departments`);
  return data;
};
export const industryCurate = async (body) => {
  // Enqueues a background curator job and returns {job, status:"queued"}
  const { data } = await api.post(`/admin/industry-library/curate`, body);
  return data;
};
export const industryCurateJobStatus = async (jobId) => {
  const { data } = await api.get(`/admin/industry-library/curate/jobs/${jobId}`);
  return data;
};
export const industryBulkSave = async (body) => {
  const { data } = await api.post(`/admin/industry-library/bulk-save`, body);
  return data;
};

// ----- MCP catalog & per-agent MCP requirements -----
export const mcpCatalog = async () => {
  const { data } = await api.get(`/mcp/catalog`);
  return data;
};
export const agentMcpRequired = async (slug) => {
  const { data } = await api.get(`/agents/${slug}/mcp/required`);
  return data;
};
export const agentMcpList = async (slug) => {
  const { data } = await api.get(`/agents/${slug}/mcp`);
  return data;
};
export const agentMcpUpsert = async (slug, tool, config) => {
  const { data } = await api.put(`/agents/${slug}/mcp/${tool}`, { config });
  return data;
};
export const agentMcpDelete = async (slug, tool) => {
  const { data } = await api.delete(`/agents/${slug}/mcp/${tool}`);
  return data;
};
export const adminMcpCustomCreate = async (body) => {
  const { data } = await api.post(`/admin/mcp/custom`, body);
  return data;
};
export const adminMcpCustomDelete = async (id) => {
  const { data } = await api.delete(`/admin/mcp/custom/${id}`);
  return data;
};

// ----- Admin · Demo data + Companies -----
export const adminDemoStatus = async () => {
  const { data } = await api.get(`/admin/demo`);
  return data;
};
export const adminDemoRestore = async () => {
  const { data } = await api.post(`/admin/demo/restore`);
  return data;
};
export const adminDemoDelete = async () => {
  const { data } = await api.delete(`/admin/demo`);
  return data;
};
export const listCompanies = async () => {
  const { data } = await api.get(`/companies`);
  return data;
};

export const adminDemoLinks = async () => {
  const { data } = await api.get(`/admin/demo/links`);
  return data;
};
export const createDemoLink = async (payload) => {
  const { data } = await api.post(`/admin/demo/links`, payload);
  return data;
};
export const revokeDemoLink = async (id) => {
  const { data } = await api.delete(`/admin/demo/links/${id}`);
  return data;
};
// Fix 12: the tour is always narrated by the browser (window.speechSynthesis, hooks/useNarration.js);
// the server mp3 narration (pregenerate button, "Voz IA" switch, /public/tour-audio) was removed.
export const getPublicDemo = async (token) => {
  const { data } = await api.get(`/public/demo/${token}`);
  return data;
};
export const publicFlowUrl = (token, fmt) => `${API_BASE}/public/demo/${token}/flow.${fmt}`;

// ----- Admin · Tenants -----
export const adminListTenants = async () => {
  const { data } = await api.get(`/admin/tenants`);
  return data;
};
export const adminUpsertTenant = async (slug, body) => {
  const { data } = await api.put(`/admin/tenants/${slug}`, body);
  return data;
};
export const adminDeleteTenant = async (slug) => {
  const { data } = await api.delete(`/admin/tenants/${slug}`);
  return data;
};

// ----- Admin · Users -----
export const adminListUsers = async (filters = {}) => {
  const { data } = await api.get(`/admin/users`, { params: filters });
  return data;
};
export const adminApproveUser = async (id) => {
  const { data } = await api.post(`/admin/users/${id}/approve`);
  return data;
};
export const adminRejectUser = async (id) => {
  const { data } = await api.post(`/admin/users/${id}/reject`);
  return data;
};
export const adminUpdateUserRole = async (id, role) => {
  const { data } = await api.put(`/admin/users/${id}/role`, { role });
  return data;
};
export const adminListUserCompanies = async () => {
  const { data } = await api.get(`/admin/users/companies`);
  return data;
};
export const adminSetUserCompany = async (id, companySlug) => {
  const { data } = await api.put(`/admin/users/${id}/company`, { company_slug: companySlug || null });
  return data;
};
export const adminDeleteUser = async (id) => {
  const { data } = await api.delete(`/admin/users/${id}`);
  return data;
};

// ----- Per-agent KBL -----
export const agentKbl = async (slug) => {
  const { data } = await api.get(`/agents/${slug}/kbl`);
  return data;
};

// ----- LLM credentials (BYO API keys) -----
export const llmProviders = async () => {
  const { data } = await api.get(`/admin/llm/providers`);
  return data;
};
export const llmGlobalKeys = async () => {
  const { data } = await api.get(`/admin/llm/keys`);
  return data;
};
export const llmGlobalSave = async (provider, { api_key, model }) => {
  const { data } = await api.put(`/admin/llm/keys/${provider}`, { api_key, model });
  return data;
};
export const llmGlobalDelete = async (provider) => {
  const { data } = await api.delete(`/admin/llm/keys/${provider}`);
  return data;
};
export const llmGlobalCheck = async (provider, { api_key, model } = {}) => {
  const { data } = await api.post(`/admin/llm/keys/${provider}/check`, { api_key, model });
  return data;
};
// fix 12: global PRIMARY / BACKUP credentials of Inviktus (no built-in key)
export const llmGlobalSlotSave = async (role, body) => (await api.put(`/admin/llm/global/${role}`, body)).data;
export const llmGlobalSlotDelete = async (role) => (await api.delete(`/admin/llm/global/${role}`)).data;
export const llmGlobalSlotCheck = async (role, body = {}) => (await api.post(`/admin/llm/global/${role}/check`, body)).data;
export const llmStatus = async () => (await api.get(`/admin/llm/status`)).data;
export const llmLegacyDelete = async (provider) => (await api.delete(`/admin/llm/legacy/${provider}`)).data;
export const agentLlmGet = async (slug) => {
  const { data } = await api.get(`/agents/${slug}/llm`);
  return data;
};
export const agentLlmSave = async (slug, { provider, api_key, model }) => {
  const { data } = await api.put(`/agents/${slug}/llm`, { provider, api_key, model });
  return data;
};
export const agentLlmDelete = async (slug) => {
  const { data } = await api.delete(`/agents/${slug}/llm`);
  return data;
};
export const agentLlmCheck = async (slug, { api_key, model } = {}) => {
  const { data } = await api.post(`/agents/${slug}/llm/check`, { api_key, model });
  return data;
};

// ---- AI Agents (one per department) ----
export const generateAgents = async (sid) => {
  const { data } = await api.post(`/sessions/${sid}/agents/generate`);
  return data;
};

export const listSessionAgents = async (sid) => {
  const { data } = await api.get(`/sessions/${sid}/agents`);
  return data;
};

export const getAgent = async (slug) => {
  const { data } = await api.get(`/agents/${slug}`);
  return data;
};

export const getAgentQa = async (slug) => (await api.get(`/agents/${slug}/qa`)).data;
export const generateAgentQa = async (slug) => (await api.post(`/agents/${slug}/qa/generate`, {}, { timeout: 120000 })).data;
export const saveAgentQa = async (slug, questions) => (await api.put(`/agents/${slug}/qa`, { questions })).data;
export const runAgentQa = async (slug) => (await api.post(`/agents/${slug}/qa/run`)).data;

export const updateAgent = async (slug, patch) => {
  const { data } = await api.put(`/agents/${slug}`, patch);
  return data;
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const chatError = (detail, code = null) =>
  Object.assign(new Error(detail), { response: { data: { detail, code } } });

// The answer is generated in the background and polled, so long replies never hit proxy timeouts.
export const startAgentMessage = async (slug, text, chatId, starterPromptId) => {
  const { data } = await api.post(`/agents/${slug}/chat`, {
    text,
    chat_id: chatId,
    starter_prompt_id: starterPromptId,
    background: true,
  });
  return data;
};

export const waitAgentReply = async (slug, chatId) => {
  const deadline = Date.now() + 6 * 60 * 1000;
  while (Date.now() < deadline) {
    await sleep(2500);
    let chat;
    try {
      chat = await getAgentChat(slug, chatId);
    } catch (e) {
      // Fix 7 (B8): lost access/session is reported at once (network blips are still retried)
      const st = e?.response?.status;
      if (st === 401 || st === 403) {
        const d = e.response.data || {};
        throw chatError(typeof d.detail === "string" ? d.detail : "Su sesión expiró o perdió el acceso a este agente.",
          d.code || "session_expired");
      }
      continue;
    }
    if (chat.pending) continue;
    if (chat.error) throw chatError(chat.error, chat.error_code || null);
    const last = chat.messages[chat.messages.length - 1];
    return last?.role === "agent" ? last : null;
  }
  throw chatError("El agente tardó demasiado en responder. Intente de nuevo.");
};

// Fix 8: «¿Le resolvió esto?» true / false / null (removes the vote)
export const rateAgentChat = async (slug, chatId, resolved) => {
  const { data } = await api.post(`/agents/${slug}/chats/${chatId}/feedback`, { resolved });
  return data;
};

// Fix 8: assumptions of the estimated savings (staff or the company master)
export const updateAgentMetricsConfig = async (slug, cfg) => {
  const { data } = await api.put(`/agents/${slug}/metrics/config`, cfg);
  return data;
};

export const getAgentChat = async (slug, chatId) => {
  const { data } = await api.get(`/agents/${slug}/chats/${chatId}`);
  return data;
};

export const getToolsCatalogue = async () => {
  const { data } = await api.get(`/tools/catalogue`);
  return data;
};

// ---- Metrics & Dashboard ----
export const getDashboard = async (window = 1) => {
  const { data } = await api.get(`/dashboard`, { params: { window } });
  return data;
};

export const getAgentMetrics = async (slug, window = 1) => {
  const { data } = await api.get(`/agents/${slug}/metrics`, { params: { window } });
  return data;
};

// ---- Admin · Client companies (step 2-3 / 4a) ----
export const adminListCompanies = async () => (await api.get(`/admin/companies`)).data;
export const adminCreateCompany = async (payload) => (await api.post(`/admin/companies`, payload)).data;
export const adminGetCompany = async (slug) => (await api.get(`/admin/companies/${slug}`)).data;
export const adminUpdateCompany = async (slug, patch) => (await api.put(`/admin/companies/${slug}`, patch)).data;
export const adminDeactivateCompany = async (slug, reason) =>
  (await api.post(`/admin/companies/${slug}/deactivate`, { reason: reason || "" })).data;
export const adminActivateCompany = async (slug) => (await api.post(`/admin/companies/${slug}/activate`)).data;
export const adminCompanyAudit = async (slug) => (await api.get(`/admin/companies/${slug}/audit`)).data;
export const adminCompanyNotifications = async (slug, limit = 20) =>
  (await api.get(`/admin/companies/${slug}/notifications`, { params: { limit } })).data;
export const adminGetInterviewLink = async (slug) => (await api.get(`/admin/companies/${slug}/interview-link`)).data;
export const adminRotateInterviewLink = async (slug) => (await api.post(`/admin/companies/${slug}/interview-link`)).data;
export const adminRevokeInterviewLink = async (slug) => (await api.delete(`/admin/companies/${slug}/interview-link`)).data;
// body: {user_id} | {email} (existing user) | {email, password, name} (new user). The password is never stored client-side.
export const adminSetCompanyMaster = async (slug, body) => (await api.post(`/admin/companies/${slug}/master`, body)).data;
export const adminUnsetCompanyMaster = async (slug) => (await api.delete(`/admin/companies/${slug}/master`)).data;
export const adminUnassignedAgents = async () => (await api.get(`/admin/agents/unassigned`)).data;
export const adminAssignAgentCompany = async (agentSlug, companySlug) =>
  (await api.put(`/admin/agents/${agentSlug}/company`, { company_slug: companySlug })).data;
export const adminAssignSessionCompany = async (sid, companySlug) =>
  (await api.put(`/admin/sessions/${sid}/company`, { company_slug: companySlug })).data;

// ---- Own company (cliente / master) ----
export const getMyCompany = async () => (await api.get(`/companies/me`)).data;
export const listCompanyAgents = async (slug) => (await api.get(`/companies/${slug}/agents`)).data;

// ---- Client LLM keys with monthly token limits (admin, or master when llm_ui_visible) ----
export const companyLlmGet = async (slug) => (await api.get(`/companies/${slug}/llm`)).data;
export const companyLlmSave = async (slug, body) => (await api.put(`/companies/${slug}/llm`, body)).data;
export const companyAgentLlmGet = async (slug, agentSlug) =>
  (await api.get(`/companies/${slug}/agents/${agentSlug}/llm`)).data;
export const companyAgentLlmSave = async (slug, agentSlug, body) =>
  (await api.put(`/companies/${slug}/agents/${agentSlug}/llm`, body)).data;
export const companyAgentLlmDelete = async (slug, agentSlug) =>
  (await api.delete(`/companies/${slug}/agents/${agentSlug}/llm`)).data;
// fix 12: backup key per slot (same monthly limit) + «Probar clave» (role: primary | backup)
export const companyLlmBackupSave = async (slug, body) => (await api.put(`/companies/${slug}/llm/backup`, body)).data;
export const companyLlmBackupDelete = async (slug) => (await api.delete(`/companies/${slug}/llm/backup`)).data;
export const companyAgentLlmBackupSave = async (slug, agentSlug, body) =>
  (await api.put(`/companies/${slug}/agents/${agentSlug}/llm/backup`, body)).data;
export const companyAgentLlmBackupDelete = async (slug, agentSlug) =>
  (await api.delete(`/companies/${slug}/agents/${agentSlug}/llm/backup`)).data;
export const companyLlmCheck = async (slug, body) => (await api.post(`/companies/${slug}/llm/check`, body)).data;
export const companyAgentLlmCheck = async (slug, agentSlug, body) =>
  (await api.post(`/companies/${slug}/agents/${agentSlug}/llm/check`, body)).data;
export const companyLlmUsage = async (slug, month) =>
  (await api.get(`/companies/${slug}/llm/usage`, { params: month ? { month } : {} })).data;

export default api;

// ---- Step 5: seats, departments and collaborator access (contracts: STEP5A_INFORME.md + 5b) ----
export const companySeats = async (slug) => (await api.get(`/companies/${slug}/seats`)).data;
export const listDepartments = async (slug) => (await api.get(`/companies/${slug}/departments`)).data;
export const createDepartment = async (slug, name) =>
  (await api.post(`/companies/${slug}/departments`, { name })).data;
export const renameDepartment = async (slug, deptId, name) =>
  (await api.put(`/companies/${slug}/departments/${deptId}`, { name })).data;
export const deleteDepartment = async (slug, deptId) =>
  (await api.delete(`/companies/${slug}/departments/${deptId}`)).data;
export const setDepartmentAgents = async (slug, deptId, agentSlugs) =>
  (await api.put(`/companies/${slug}/departments/${deptId}/agents`, { agent_slugs: agentSlugs })).data;
export const listDepartmentMembers = async (slug, deptId) =>
  (await api.get(`/companies/${slug}/departments/${deptId}/members`)).data;
export const addDepartmentMember = async (slug, deptId, email) =>
  (await api.post(`/companies/${slug}/departments/${deptId}/members`, { email })).data;
export const removeDepartmentMember = async (slug, deptId, email) =>
  (await api.delete(`/companies/${slug}/departments/${deptId}/members/${encodeURIComponent(email)}`)).data;

// Collaborator access by e-mail + 6-digit code (public). The session is an httpOnly cookie
// `iv_access_<company>` set by the backend; the token in the JSON is not stored by the SPA.
export const accessRequestCode = async (agentSlug, email) =>
  (await api.post(`/access/request-code`, { agent_slug: agentSlug, email })).data;
export const accessVerify = async (agentSlug, email, code) =>
  (await api.post(`/access/verify`, { agent_slug: agentSlug, email, code })).data;
export const accessLogout = async (agentSlug) =>
  (await api.post(`/access/logout`, agentSlug ? { agent_slug: agentSlug } : {})).data;
export const accessMe = async (agentSlug) =>
  (await api.get(`/access/me`, { params: agentSlug ? { agent_slug: agentSlug } : {} })).data;

// Error helper for the step 5 endpoints: {status, code, detail} from FastAPI ({detail}) or
// JSONResponse ({code, detail}) bodies; `retryAfter` (seconds) from the header when present.
export const apiError = (e, fallback = "No se pudo completar la operación.") => {
  const r = e?.response;
  const d = r?.data || {};
  const detail = typeof d.detail === "string" ? d.detail : (d.detail?.detail || fallback);
  const ra = Number(r?.headers?.["retry-after"]);
  return { status: r?.status || 0, code: d.code || d.detail?.code || null, detail,
    retryAfter: Number.isFinite(ra) && ra > 0 ? ra : null };
};
